import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import Shepherd from 'shepherd.js';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class TourService {
  private tour!: Shepherd.Tour;
  private tourData: any = {};
  private readonly TOUR_STORAGE_KEY = 'dontShowTour';

  constructor(private http: HttpClient, private translate: TranslateService) {
    this.loadTourSteps();
    this.initTour();
  }

  /**
   * Initializes the Shepherd.js tour with initial options.
   */
  private initTour() {
    this.tour = new Shepherd.Tour({
      tourName: 'my-tour', // A unique tour name
      exitOnEsc: true, // Exit the tour when the ESC key is pressed
      keyboardNavigation: true, // Allow navigation via keyboard
      defaultStepOptions: {
        cancelIcon: { enabled: false },
        scrollTo: { behavior: 'smooth', block: 'center' },
        classes: 'custom-tour-dialog',
      },
      useModalOverlay: true,
    });

    this.tour.on('cancel', () => {
      localStorage.setItem(this.TOUR_STORAGE_KEY, 'true');
      console.log('Tour closed and will not show again until manually enabled.');
    });

  }

  /**
   * Loads the tour steps JSON dynamically from assets.
   */
  private loadTourSteps() {
    this.http.get('assets/tour.json').subscribe((data) => {
      this.tourData = data;
    });
  }

  /**
   * Starts the tour for a specific module using JSON-defined steps.
   * @param moduleName The name of the module (e.g., 'TOUR_STEPS_DASHBOARD')
   */
  public startTour(moduleName: string) {

    if (!this.tourData[moduleName]) {
        console.error(`Tour steps for ${moduleName} not found!`);
        return;
    }

    // Clear any existing steps
    this.tour.steps = [];

    // Add steps from the loaded JSON data
    this.tourData[moduleName].forEach((step: any) => {
      const formattedStep: any = {
          id: step.id,
          title: this.translate.instant(step.title),
          text: this.translate.instant(step.text),
          buttons: step.buttons.map((btn: any) => ({
              text: this.translate.instant(btn.text),
              action: () => this.handleButtonAction(btn.action), // Ensuring function execution
              classes: btn.classes || '',
          }))
      };
  
      let elementExists = true; // Default to true if there's no attachTo
  
      if (step.attachTo) {
          formattedStep.attachTo = { element: step.attachTo.element, on: step.attachTo.on };
          elementExists = !!document.querySelector(formattedStep.attachTo.element); // Check if the element exists
      }
  
      if (elementExists) {
          this.tour.addStep(formattedStep);
      }
    });
  
    this.tour.start();
  }

  /**
   * Handles button actions dynamically.
   */
  private handleButtonAction(action: string) {
      if (action === 'next') this.tour.next();
      else if (action === 'back') this.tour.back();
      else if (action === 'cancel') this.tour.cancel();
      else if (action === 'complete') this.tour.complete();
  }

  public startTourFromActiveTab(activeTabId: string, moduleName: string) {
    console.log(`Starting tour from tab: ${activeTabId}`);

    if (!this.tourData[moduleName]) {
        console.error(`Tour steps for ${moduleName} not found!`);
        return;
    }

    // Log available IDs for debugging
    console.log('Available tour step IDs:', this.tourData[moduleName].map((step: any) => step.id));

    // Find the exact step where the active tab matches
    const activeStep = this.tourData[moduleName].find((step: any) => 
        step.attachTo && step.attachTo.element === `#${activeTabId}`
    );

    if (!activeStep) {
        console.error(`No matching tour step found for active tab ID: ${activeTabId}`);
        return;
    }

    const activeTabIndex = this.tourData[moduleName].indexOf(activeStep);

    if (activeTabIndex === -1) {
        console.error(`Tab ${activeTabId} not found in tour steps!`);
        return;
    }

    // Clear existing steps before restarting the tour
    this.tour.steps = [];

    // Convert string actions to functions dynamically
    const actionMap: { [key: string]: Function } = {
        next: () => this.tour.next(),
        back: () => this.tour.back(),
        cancel: () => this.tour.cancel(),
        complete: () => this.tour.complete(),
    };

    // Create a **new array** of steps to avoid modifying the original data
    const stepsToUse = this.tourData[moduleName].slice(activeTabIndex).map((step: any) => {
        const newStep = { ...step }; // Clone step to prevent mutation

        newStep.buttons = step.buttons.map((button: any) => ({
            ...button,
            action: actionMap[button.action] || (() => {}), // Map actions
        }));

        // Ensure only one "Close" button is added
        if (!newStep.buttons.some((btn: any) => btn.text.includes("Close"))) {
            newStep.buttons.push({
                text: `<i class="fa fa-times"></i> Close`,
                action: () => this.tour.cancel(),
                classes: 'tour-cancel-button',
            });
        }

        return newStep;
    });

    // Add only steps that have a valid attached element
    stepsToUse.forEach((step: any) => {
        const elementExists = step.attachTo ? document.querySelector(step.attachTo.element) : true;
        if (elementExists) {
            this.tour.addStep(step);
        }
    });

    this.tour.start();
  }



  /**
   * Starts the tour for a specific module by mapping menu items to steps.
   * Uses Font Awesome icons for the navigation buttons.
   * @param menus The array of menu definitions.
   */
  public startMenuTour(menus: any[]) {
    if (localStorage.getItem(this.TOUR_STORAGE_KEY) === 'true') {
      console.log('Tour is disabled by the user.');
      return;
    }

    // Clear existing steps
    this.tour.steps = [];
    const steps = menus.map((menu, index) => {
      const elementSelector = '#tour-' + menu.id;
      const elementExists = document.querySelector(elementSelector);

      // Build the button configuration with a conditional Back button
      const buttons: any[] = [];

      // Add Back button if this is not the first step
      if (index > 0) {
        buttons.push({
          text: `<i class="fa fa-arrow-left"></i> ${this.translate.instant('Back')}`,
          action: () => this.tour.back(),
          classes: 'tour-back-button',
        });
      }

      // Always include Next and Cancel buttons
      buttons.push(
        {
          text: `<i class="fa fa-arrow-right"></i> ${this.translate.instant('Next')}`,
          action: () => this.tour.next(),
          classes: 'tour-next-button',
        },
        {
          text: `<i class="fa fa-times"></i> ${this.translate.instant('Cancel')}`,
          action: () => this.tour.cancel(),
          classes: 'tour-cancel-button',
        }
      );

      console.log(menu);

      // Build the step configuration
      const step: any = {
        id: 'tour-' + menu.id,
        title: this.translate.instant(menu.title) || 'Guided Tour', // Step title
        text: menu.text || this.translate.instant(menu.description), // Step description
        classes: 'custom-tour-class', // Optional styling
        buttons: buttons,
      };

      // Only attach the step to an element if it exists in the DOM
      if (elementExists) {
        step.attachTo = {
          element: elementSelector,
          on: (menu.attachTo?.on || 'top'), // Default attachment placement
        };
      }

      return step;
    });

    // Add each step to the tour
    steps.forEach((step: any) => {
      this.tour.addStep(step);
    });

    this.addDontShowAgainStep();

    this.tour.start();
  }

  /**
   * Adds the final step asking if the user wants to disable the tour.
   */
  private addDontShowAgainStep() {
    this.tour.addStep({
      id: 'dont-show-again',
      title: this.translate.instant('Tour Settings'),
      text: `
        <p>${this.translate.instant('Would you like to disable the tour until manually re-enabled?')}</p>
        <label>
          <input type="checkbox" id="dont-show-tour-checkbox">
          ${this.translate.instant('Do not show again')}
        </label>
      `,
      buttons: [
        {
          text: this.translate.instant('Finish'),
          action: () => {
            const checkbox = document.getElementById(
              'dont-show-tour-checkbox'
            ) as HTMLInputElement;
            if (checkbox && checkbox.checked) {
              localStorage.setItem(this.TOUR_STORAGE_KEY, 'true');
            }
            this.tour.complete();
          },
          classes: 'tour-finish-button',
        },
      ],
    });
  }

  public enableTourAgain() {
    localStorage.removeItem(this.TOUR_STORAGE_KEY);
    console.log('Tour has been enabled again.');
  }
}
